import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const ContestManagement = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [contests, setContests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    startDate: '',
    endDate: ''
  });
  const [saving, setSaving] = useState(false);

  const isAdmin = useMemo(
    () => user?.systemRoles?.some((role) => role.code === 'SYSTEM_ADMIN' || role.code === 'SUPER_ADMIN'),
    [user]
  );

  useEffect(() => {
    fetchContests();
  }, []);

  const fetchContests = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/contests');
      setContests(response.data || []);
    } catch (err) {
      console.error('Failed to fetch contests:', err);
      setError('Failed to load challenges. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateContest = async (event) => {
    event.preventDefault();
    try {
      setSaving(true);
      setError('');
      await axios.post('/api/contests', formData);
      setFormData({ name: '', startDate: '', endDate: '' });
      setShowCreateForm(false);
      fetchContests();
    } catch (err) {
      console.error('Failed to create contest:', err);
      setError(err.response?.data?.message || 'Failed to create challenge. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="page"><div className="loading">Loading challenges...</div></div>;
  }

  const today = new Date().toISOString().slice(0, 10);
  const totalContests = contests.length;
  const activeContests = contests.filter((contest) => contest.startDate <= today && contest.endDate >= today).length;
  const totalParticipants = contests.reduce((sum, contest) => sum + (contest.participantCount || 0), 0);

  return (
    <div className="page">
      <div className="card" style={{ padding: '24px', marginBottom: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: '240px' }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '6px 12px', background: 'var(--pale-blue)', color: 'var(--dark-blue)', borderRadius: '999px', fontSize: '12px', fontWeight: 700, marginBottom: '14px' }}>
              Challenge Hub
            </div>
            <h1 className="page-title" style={{ marginBottom: '8px' }}>{isAdmin ? 'Challenge Management' : 'Challenges'}</h1>
            <p style={{ color: 'var(--gray-dark)', margin: 0, lineHeight: 1.6 }}>
              Review challenge windows, track participation, and open each challenge for detailed management.
            </p>
          </div>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            {isAdmin && (
              <button className="btn btn-primary" onClick={() => setShowCreateForm((value) => !value)}>
                {showCreateForm ? 'Close Form' : 'Add Challenge'}
              </button>
            )}
            <Link to="/dashboard" className="btn btn-secondary">Back to Dashboard</Link>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '14px', marginTop: '20px' }}>
          <div style={{ background: 'var(--gray-light)', border: '1px solid var(--gray-medium)', borderRadius: '10px', padding: '16px' }}>
            <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--gray-dark)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '6px' }}>
              Total Challenges
            </div>
            <div style={{ fontSize: '28px', fontWeight: 700, color: 'var(--dark-blue)' }}>
              {totalContests}
            </div>
          </div>
          <div style={{ background: 'var(--gray-light)', border: '1px solid var(--gray-medium)', borderRadius: '10px', padding: '16px' }}>
            <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--gray-dark)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '6px' }}>
              Active Now
            </div>
            <div style={{ fontSize: '28px', fontWeight: 700, color: 'var(--dark-blue)' }}>
              {activeContests}
            </div>
          </div>
          <div style={{ background: 'var(--gray-light)', border: '1px solid var(--gray-medium)', borderRadius: '10px', padding: '16px' }}>
            <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--gray-dark)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '6px' }}>
              Total Participants
            </div>
            <div style={{ fontSize: '28px', fontWeight: 700, color: 'var(--dark-blue)' }}>
              {totalParticipants}
            </div>
          </div>
        </div>
      </div>

      {error && <div className="alert alert-error mb-20">{error}</div>}

      {showCreateForm && isAdmin && (
        <div className="card mb-20" style={{ padding: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap', marginBottom: '18px' }}>
            <div>
              <h3 style={{ color: 'var(--dark-blue)', margin: '0 0 6px 0' }}>Create Challenge</h3>
              <p style={{ margin: 0, color: 'var(--gray-dark)', fontSize: '14px' }}>
                Set the name and date window for a new challenge before managing participants.
              </p>
            </div>
            <div style={{ fontSize: '13px', color: 'var(--gray-dark)', background: 'var(--gray-light)', border: '1px solid var(--gray-medium)', borderRadius: '999px', padding: '8px 12px' }}>
              Admin Only
            </div>
          </div>
          <form onSubmit={handleCreateContest}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px' }}>
              <div>
                <label className="form-label">Challenge Name</label>
                <input
                  className="form-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="form-label">Start Date</label>
                <input
                  type="date"
                  className="form-input"
                  value={formData.startDate}
                  onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="form-label">End Date</label>
                <input
                  type="date"
                  className="form-input"
                  value={formData.endDate}
                  onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                  required
                />
              </div>
            </div>
            <div style={{ marginTop: '16px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <button className="btn btn-primary" type="submit" disabled={saving}>
                {saving ? 'Creating...' : 'Create Challenge'}
              </button>
              <button className="btn btn-secondary" type="button" onClick={() => setShowCreateForm(false)}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="card" style={{ padding: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap', marginBottom: '18px' }}>
          <div>
            <h3 style={{ color: 'var(--dark-blue)', margin: '0 0 6px 0' }}>All Challenges</h3>
            <p style={{ margin: 0, color: 'var(--gray-dark)', fontSize: '14px' }}>
              Open any challenge to review participants and manage the contest window in detail.
            </p>
          </div>
          <div style={{ fontSize: '13px', color: 'var(--gray-dark)', background: 'var(--gray-light)', border: '1px solid var(--gray-medium)', borderRadius: '999px', padding: '8px 12px' }}>
            Total: {totalContests}
          </div>
        </div>

        {contests.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '42px 20px', color: 'var(--gray-dark)', background: 'var(--gray-light)', borderRadius: '10px', border: '1px dashed var(--gray-medium)' }}>
            No challenges created yet.
          </div>
        ) : (
          <div style={{ overflowX: 'auto', border: '1px solid var(--gray-medium)', borderRadius: '12px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
              <thead>
                <tr style={{ backgroundColor: 'var(--pale-blue)' }}>
                  <th style={{ padding: '14px 16px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>Challenge</th>
                  <th style={{ padding: '14px 16px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>Date Range</th>
                  <th style={{ padding: '14px 16px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>Participants</th>
                  <th style={{ padding: '14px 16px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>Status</th>
                  <th style={{ padding: '14px 16px', textAlign: 'center', borderBottom: '2px solid var(--light-blue)' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {contests.map((contest, index) => {
                  const isActive = contest.startDate <= today && contest.endDate >= today;
                  const isUpcoming = contest.startDate > today;

                  return (
                    <tr
                      key={contest.id}
                      style={{
                        backgroundColor: index % 2 === 0 ? 'var(--white)' : 'var(--gray-light)',
                        borderBottom: '1px solid var(--gray-medium)'
                      }}
                    >
                      <td style={{ padding: '14px 16px' }}>
                        <div style={{ fontWeight: 600, color: 'var(--dark-blue)', marginBottom: '4px' }}>
                          {contest.name}
                        </div>
                        <div style={{ fontSize: '12px', color: 'var(--gray-dark)' }}>
                          Contest ID: {contest.id}
                        </div>
                      </td>
                      <td style={{ padding: '14px 16px', color: 'var(--gray-dark)' }}>
                        <strong>{contest.startDate}</strong> to <strong>{contest.endDate}</strong>
                      </td>
                      <td style={{ padding: '14px 16px', color: 'var(--dark-blue)', fontWeight: 600 }}>
                        {contest.participantCount || 0}
                      </td>
                      <td style={{ padding: '14px 16px' }}>
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            padding: '4px 10px',
                            borderRadius: '999px',
                            fontSize: '12px',
                            fontWeight: 700,
                            color: isActive ? 'var(--success)' : isUpcoming ? 'var(--primary-blue)' : 'var(--gray-dark)',
                            background: isActive ? '#F0FFF4' : isUpcoming ? '#EBF8FF' : 'var(--gray-light)'
                          }}
                        >
                          {isActive ? 'ACTIVE' : isUpcoming ? 'UPCOMING' : 'COMPLETED'}
                        </span>
                      </td>
                      <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                        <button className="btn btn-secondary" onClick={() => navigate(`/contests/${contest.id}`)}>
                          Open
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContestManagement;
